import { CircularProgress } from '@mui/material';
import React, { FC, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import placeholderAvatar from '/assets/pngs/place-holder-avatar.png';

import { isErrorInfo } from '@/shared/interfaces/error-info.interface';
import {
  userImageRequest,
  userImageSuccess,
  userImageFailure
} from '@/shared/redux/actions/auth.actions';
import { RootState } from '@/shared/redux/reducers';
import {
  getCurrentUserImage,
  getUserImageById
} from '@/shared/services/data/user-data/user-image.data';

import {
  ImageContainer,
  AuthImage,
  LoaderContainer,
  ImageSkeleton
} from './image-with-authorization.styles';
import { ImageWithAuthorizationProps, ImageLoadState } from './image-with-authorization.types';

/**
 * ImageWithAuthorization component that fetches images with authorization headers
 * and displays them with proper loading and error states.
 * Uses Redux to cache user image URL and prevent multiple API calls.
 *
 * @param data - Contains imageUrl and alt text
 * @param config - Optional styling and behavior configuration
 * @param eventHandlers - Optional event callbacks
 * @param styles - Optional inline styles
 */
const ImageWithAuthorization: FC<ImageWithAuthorizationProps> = ({
  data,
  config = {},
  eventHandlers = {},
  styles = {}
}) => {
  const { currentUserImage, userId, alt = 'Image' } = data;
  const {
    width = 'auto',
    height = 'auto',
    objectFit = 'cover',
    borderRadius = 0,
    showLoader = true
  } = config;
  const { onLoad, onError, onClick } = eventHandlers;

  const dispatch = useDispatch();
  const { imageUrl: cachedImageUrl, imageLoading } = useSelector((state: RootState) => state.auth);

  const [imageState, setImageState] = useState<ImageLoadState>({
    loading: false,
    loaded: false,
    error: null,
    dataUrl: null
  });

  /**
   * Fetches the image URL using the user API service and caches it in Redux
   */
  const fetchImageWithAuth = useCallback(
    async (currentUserImage: boolean, userId?: string) => {
      // Check if we already have the image URL in Redux store (for current user only)
      if (currentUserImage && cachedImageUrl && !imageLoading) {
        console.log('🎯 Using cached image URL from Redux store');
        setImageState({
          loading: false,
          loaded: true,
          error: null,
          dataUrl: cachedImageUrl
        });
        onLoad?.();
        return;
      }

      // Don't make API call if already loading
      if (imageLoading) {
        console.log('⏳ Image already loading, skipping API call');
        return;
      }

      console.log('🚀 Fetching image from API and caching in Redux');

      // Dispatch loading state to Redux
      if (currentUserImage) {
        dispatch(userImageRequest());
      }

      setImageState((prev) => ({
        ...prev,
        loading: true,
        error: null
      }));

      try {
        let imageUrl: string;

        // Check if it's a user image endpoint
        if (currentUserImage) {
          // Use the data service for current user image
          const result = await getCurrentUserImage('ImageWithAuthorization');

          if (isErrorInfo(result)) {
            const errorMsg = `Failed to load image: ${result.errorMessage}`;
            dispatch(userImageFailure(result));
            setImageState((prev) => ({
              ...prev,
              loading: false,
              error: errorMsg
            }));
            onError?.(errorMsg);
            return;
          } else {
            imageUrl = result as string;
            // Cache the image URL in Redux for current user
            dispatch(userImageSuccess({ imageUrl }));
            console.log('✅ Image URL cached in Redux store');
          }
        } else if (userId) {
          // Get specific user image URL (not cached in Redux)
          const result = await getUserImageById(userId, 'ImageWithAuthorization');

          if (isErrorInfo(result)) {
            const errorMsg = `Failed to load image: ${result.errorMessage}`;
            setImageState((prev) => ({
              ...prev,
              loading: false,
              error: errorMsg
            }));
            onError?.(errorMsg);
            return;
          } else {
            imageUrl = result as string;
          }
        } else {
          // For non-user images, this component currently only supports user images
          const errorMsg = 'This component currently only supports user image endpoints';
          if (currentUserImage) {
            dispatch(
              userImageFailure({
                errorCode: 'UNSUPPORTED_IMAGE_TYPE',
                errorMessage: errorMsg
              })
            );
          }
          setImageState((prev) => ({
            ...prev,
            loading: false,
            error: errorMsg
          }));
          onError?.(errorMsg);
          return;
        }

        // Now that we have the URL, load the image
        const img = new Image();

        img.onload = () => {
          setImageState({
            loading: false,
            loaded: true,
            error: null,
            dataUrl: imageUrl
          });
          onLoad?.();
        };

        img.onerror = () => {
          const errorMsg = 'Failed to load image from URL';
          if (currentUserImage) {
            dispatch(
              userImageFailure({
                errorCode: 'IMAGE_LOAD_FAILED',
                errorMessage: errorMsg
              })
            );
          }
          setImageState((prev) => ({
            ...prev,
            loading: false,
            error: errorMsg
          }));
          onError?.(errorMsg);
        };

        // Start loading the image
        img.src = imageUrl;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to load image';
        if (currentUserImage) {
          dispatch(
            userImageFailure({
              errorCode: 'IMAGE_FETCH_ERROR',
              errorMessage: errorMsg
            })
          );
        }
        setImageState((prev) => ({
          ...prev,
          loading: false,
          error: errorMsg
        }));
        onError?.(errorMsg);
      }
    },
    [onLoad, onError, cachedImageUrl, imageLoading, dispatch]
  );

  /**
   * Handles fallback image loading if primary image fails
   */
  const handleImageError = useCallback(() => {
    if ((currentUserImage || userId) && !imageState.error) {
      fetchImageWithAuth(currentUserImage, userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserImage, userId, imageState.error]);
  /**
   * Initialize image loading when component mounts or imageUrl changes
   */
  useEffect(() => {
    if (currentUserImage || userId) {
      fetchImageWithAuth(currentUserImage, userId);
    } else {
      setImageState({
        loading: false,
        loaded: false,
        error: 'No image URL provided',
        dataUrl: null
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserImage, userId, cachedImageUrl]);

  /**
   * Container styles combining config and inline styles
   */
  const containerStyles: React.CSSProperties = {
    width,
    height,
    borderRadius,
    ...styles
  };

  /**
   * Image styles
   */
  const imageStyles: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit,
    borderRadius
  };

  /**
   * Render loading state
   */
  if ((imageState.loading || imageLoading) && showLoader) {
    return (
      <ImageContainer style={containerStyles}>
        <ImageSkeleton
          variant="rectangular"
          width={width}
          height={height}
          style={{ borderRadius }}
        />
        <LoaderContainer>
          <CircularProgress size={24} />
        </LoaderContainer>
      </ImageContainer>
    );
  }

  /**
   * Render error state with placeholder avatar
   */
  if (imageState.error) {
    return (
      <ImageContainer style={containerStyles}>
        <AuthImage src={placeholderAvatar} alt={alt} style={imageStyles} />
      </ImageContainer>
    );
  }

  /**
   * Render loaded image
   */
  if (imageState.loaded && imageState.dataUrl) {
    return (
      <ImageContainer style={containerStyles}>
        <AuthImage
          src={imageState.dataUrl}
          alt={alt}
          onError={handleImageError}
          onClick={onClick}
          style={{
            ...imageStyles,
            cursor: onClick ? 'pointer' : 'default'
          }}
        />
      </ImageContainer>
    );
  }

  /**
   * Render placeholder when no image is loaded
   */
  return (
    <ImageContainer style={containerStyles}>
      <AuthImage src={placeholderAvatar} alt={alt} style={imageStyles} />
    </ImageContainer>
  );
};

export default ImageWithAuthorization;
